import React from "react";
import { Users, Package, CheckCircle, BarChart3, Settings, UserPlus, Plus, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ActivityLogsCard from "../ActivityLogs/ActivityLogsCard";

export default function DashboardOverview({
  totalUsers,
  totalProducts,
  totalBatches,
  currentUser
}) {
  const navigate = useNavigate();

  return (
    <div className="animate-fade-in px-2">

      {/* TOP METRICS */}
      <div className="row g-3 mb-4">

        <div className="col-md-6 col-xl-3">
          <MetricCard
            title="Total Users"
            value={totalUsers}
            icon={<Users size={20}/>}
            color="primary"
          />
        </div>

        <div className="col-md-6 col-xl-3">
          <MetricCard
            title="Active Products"
            value={totalProducts}
            icon={<Package size={20}/>}
            color="success"
          />
        </div>

        <div className="col-md-6 col-xl-3">
          <MetricCard
            title="Total Batches"
            value={totalBatches}
            icon={<CheckCircle size={20}/>}
            color="warning"
          />
        </div>

        <div className="col-md-6 col-xl-3">
          <MetricCard
            title="Monthly Revenue"
            value="—"
            icon={<BarChart3 size={20}/>}
            color="info"
          />
        </div>

      </div>

      <div className="row g-4">

        {/* SHORTCUTS */}
        <div className="col-lg-7">
          <div className="card border-0 shadow-sm rounded-4 h-100 overflow-hidden">

            <div className="card-header bg-white border-0 pt-4 px-4 pb-0">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center gap-2">
                  <div className="bg-primary bg-opacity-10 p-2 rounded-2">
                    <Settings size={18} className="text-primary" />
                  </div>
                  <h6 className="fw-bold mb-0 text-dark">Management Shortcuts</h6>
                </div>
                <span className="tiny-label text-muted opacity-50">Quick Access</span>
              </div>
            </div>

            <div className="card-body p-4">
              <div className="row g-3">

                {currentUser.role === "ADMIN" && (
                  <div className="col-md-6">
                    <ShortcutButton
                      onClick={() => navigate("/AdminDashboard/users")}
                      icon={<UserPlus size={22} />}
                      title="User Management"
                      sub="Add New Staff"
                      color="success"
                    />
                  </div>
                )}

                <div className="col-md-6">
                  <ShortcutButton
                    onClick={() => navigate("/AdminDashboard/products/add")}
                    icon={<Plus size={22} />}
                    title="Product Setup"
                    sub="Register new Product"
                    color="primary"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ACTIVITY LOGS */}
        <div className="col-lg-5">
          <ActivityLogsCard />
        </div>

      </div>

      <style>{`
        .shadow-xs { box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
        .tiny-label { font-size: 0.65rem; letter-spacing: 0.8px; font-weight: 700; text-transform: uppercase; }
        .hover-lift { transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1); }
        .hover-lift:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.06); }
        .icon-box { transition: transform 0.3s ease; }
        .hover-lift:hover .icon-box { transform: scale(1.1); }
        .font-numeric { font-family: 'Inter', system-ui, -apple-system, sans-serif; letter-spacing: -0.5px; }
      `}</style>

    </div>
  );
}

function MetricCard({ title, value, icon, color }) {
  return (
    <div className="card border-0 shadow-sm rounded-4 h-100 position-relative overflow-hidden transition-all hover-card-premium">
      {/* Decorative accent background blob */}
      <div 
        className={`position-absolute top-0 end-0 p-4 opacity-10 bg-${color}`} 
        style={{ borderRadius: '0 0 0 100%', marginTop: '-10px', marginRight: '-10px' }}
      ></div>

      <div className="card-body p-4 position-relative" style={{ zIndex: 1 }}>
        <div className="d-flex align-items-center mb-3">
          {/* Refined Icon Box - No heavy colors, just clean contrast */}
          <div 
            className={`d-flex align-items-center justify-content-center rounded-3 bg-${color} bg-opacity-10 text-${color}`}
            style={{ width: '42px', height: '42px' }}
          >
            {icon}
          </div>
          
          <div className="ms-3">
             <div className="tiny-label text-muted text-uppercase fw-bold" style={{ fontSize: '0.65rem', letterSpacing: '0.8px' }}>
               {title}
             </div>
          </div>
        </div>

        <div className="d-flex align-items-baseline">
          <h2 className="fw-bold text-dark mb-0 font-numeric" style={{ letterSpacing: '-0.5px' }}>
            {value || "0"}
          </h2>
          {/* Subtle status indicator dot */}
          <span className={`ms-2 d-inline-block rounded-circle bg-${color}`} style={{ width: '6px', height: '6px' }}></span>
        </div>
      </div>

      <style>{`
        .hover-card-premium {
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
        }
        .hover-card-premium:hover {
          transform: translateY(-5px);
          box-shadow: 0 12px 24px rgba(0,0,0,0.08) !important;
        }
        .font-numeric {
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
      `}</style>
    </div>
  );
}

function ShortcutButton({ onClick, icon, title, sub, color }) {
  return (
    <button
      onClick={onClick}
      className="btn btn-white border border-light-subtle w-100 p-3 text-start d-flex align-items-center rounded-4 shadow-xs hover-lift"
      style={{ borderLeft: `4px solid var(--bs-${color})` }}
    >
      <div className={`bg-${color} bg-opacity-10 p-3 rounded-3 me-3 text-${color} icon-box`}>
        {icon}
      </div>

      <div className="flex-grow-1">
        <div className="fw-bold text-dark small">{title}</div>
        <div className="text-muted" style={{ fontSize: "0.7rem", opacity: 0.8 }}>{sub}</div>
      </div>

      <ChevronRight size={14} className="text-muted opacity-25" />
    </button>
  );
}
